import { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { captureException } from '../../../observability/sentry';

// Los diccionarios viven en ../i18n/{es,en}.js y se cargan bajo demanda (un chunk por idioma)
// para no inflar main.js. Ver docs/plan-plataforma-velocidad-2026-Q4.md, punto 2.1.
const LanguageContext = createContext();
const STORAGE_KEY = 'shofi_lang';
const cache = new Map();

const readLang = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) || 'es';
  } catch (e) {
    return 'es';
  }
};

const writeLang = (lang) => {
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch (e) {
    // Almacenamiento no disponible (modo privado, cuota): el idioma vive solo en memoria.
  }
};

// import() explícito por idioma (no template string) para que webpack genere exactamente dos chunks.
const loadDictionary = (lang) => {
  if (cache.has(lang)) return Promise.resolve(cache.get(lang));
  const loader = lang === 'en' ? import('../i18n/en') : import('../i18n/es');
  return loader
    .then((mod) => mod.default)
    .catch((err) => {
      captureException(err, { where: 'LanguageContext.loadDictionary', lang });
      return {};
    })
    .then((dict) => {
      cache.set(lang, dict);
      return dict;
    });
};

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(readLang);
  const [dict, setDict] = useState(() => cache.get(lang) || null);

  useEffect(() => {
    let active = true;
    loadDictionary(lang).then((loaded) => {
      if (active) setDict(loaded);
    });
    return () => { active = false; };
  }, [lang]);

  const toggleLang = useCallback(() => {
    setLang((prev) => {
      const next = prev === 'es' ? 'en' : 'es';
      writeLang(next);
      return next;
    });
  }, []);

  const t = useCallback((key) => dict?.[key] ?? key, [dict]);
  const value = useMemo(() => ({ lang, toggleLang, t }), [lang, toggleLang, t]);

  // Hasta el primer diccionario no se pintan las pantallas (evita mostrar claves crudas).
  if (!dict) return null;
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
}
